import { CCApiService } from './api-service';
import { UIConfig } from './config-service';
import { ServiceAccount, ServiceAccountForm } from '../../components/service-accounts-table-card/interfaces';
import { Role } from '../../models/role/role.model';

export class ServiceAccountsService {
  static ApiService: CCApiService;
  static serviceAccountsAPI: string;
  static introspectionAPI: string;
  static apiBasePath: string;

  static init(config: UIConfig) {
    this.ApiService = new CCApiService(config.authClientId, config.authIssuerUri);
    this.apiBasePath = config.apiBasePath;
    this.introspectionAPI = `${this.apiBasePath}/introspection`;
  }

  static async getServiceAccount(customerId, id): Promise<ServiceAccount> {
    this.serviceAccountsAPI = `${this.apiBasePath}/customers/${customerId}/serviceaccounts`;

    return this.ApiService.performFetch(`${this.serviceAccountsAPI}/${id}`).then((entities: ServiceAccount) => {
      return entities;
    });
  }

  static async getServiceAccounts(customerId, filter: Record<string, string>): Promise<any> {
    this.serviceAccountsAPI = `${this.apiBasePath}/customers/${customerId}/serviceaccounts`;

    return this.ApiService.performFetch(`${this.serviceAccountsAPI}?${new URLSearchParams(filter)}`).then(
      (entities) => {
        return entities;
      }
    );
  }

  static async getServiceAccountCredentials(customerId, id): Promise<{ clientId: string; clientSecret: string }> {
    this.serviceAccountsAPI = `${this.apiBasePath}/customers/${customerId}/serviceaccounts/${id}/credentials`;

    return this.ApiService.performFetch(`${this.serviceAccountsAPI}`).then(
      (entities: { clientId: string; clientSecret: string }) => {
        return entities;
      }
    );
  }

  static async createServiceAccount(customerId, serviceAccount: ServiceAccountForm): Promise<ServiceAccount> {
    const fetchOptions = {
      method: 'POST',
      body: JSON.stringify(serviceAccount),
    };
    this.serviceAccountsAPI = `${this.apiBasePath}/customers/${customerId}/serviceaccounts`;

    return this.ApiService.performFetch(this.serviceAccountsAPI, fetchOptions).then((entity: ServiceAccount) => {
      return entity;
    });
  }

  static async updateServiceAccount(customerId, serviceAccount: ServiceAccountForm): Promise<ServiceAccount> {
    const fetchOptions = {
      method: 'PUT',
      body: JSON.stringify(serviceAccount),
    };
    this.serviceAccountsAPI = `${this.apiBasePath}/customers/${customerId}/serviceaccounts`;
    return this.ApiService.performFetch(`${this.serviceAccountsAPI}/${serviceAccount.id}`, fetchOptions).then(
      (entity: ServiceAccount) => {
        return entity;
      }
    );
  }

  static async deleteServiceAccount(customerId, id: string): Promise<void> {
    const fetchOptions = {
      method: 'DELETE',
    };
    this.serviceAccountsAPI = `${this.apiBasePath}/customers/${customerId}/serviceaccounts`;
    return this.ApiService.performFetch(`${this.serviceAccountsAPI}/${id}`, fetchOptions);
  }

  static async getScopes(): Promise<{ scopes: [{ name: string; description: string }] }> {
    return this.ApiService.performFetch(`${this.introspectionAPI}/scopes`).then(
      (entities: { scopes: [{ name: string; description: string }] }) => {
        return entities;
      }
    );
  }

  // Returns only UI-login (web-flow) service accounts: MANAGED_FUSION type with at least one login URL.
  static async getWebFlowServiceAccounts(customerId: string): Promise<ServiceAccount[]> {
    const page = await this.getServiceAccounts(customerId, { size: '100' });
    const all: ServiceAccount[] = page?.content ?? [];
    return all.filter((sa) => sa.type === 'MANAGED_FUSION' && (sa.loginUrls?.length ?? 0) > 0);
  }

  // Assigns MF roles to a user scoped to a service account via okta-worker's SA-user route.
  // Pass an empty array to revoke all SA-scoped roles for this user at this service account.
  static async assignServiceAccountRole(
    customerId: string,
    serviceAccountId: string,
    userEmail: string,
    roles: Role[]
  ): Promise<void> {
    const fetchOptions = {
      method: 'PATCH',
      body: JSON.stringify({
        assignedRoles: roles.map((r) => ({ level: 'SERVICE_ACCOUNT', id: serviceAccountId, role: r.role })),
      }),
    };
    return this.ApiService.performFetch(
      `${this.apiBasePath}/customers/${customerId}/serviceaccounts/${serviceAccountId}/users/${userEmail}`,
      fetchOptions
    );
  }
}
